/**
 * State Preservation Service
 *
 * Saves and restores application state when authentication is required.
 * This allows users to return to their previous state after token renewal.
 */

interface PreservedState {
  pathname: string;
  search: string;
  hash: string;
  state: Record<string, any>;
  timestamp: number;
}

class StatePreservationService {
  private readonly STATE_KEY = 'preserved_app_state';
  private readonly MAX_STATE_AGE = 30 * 60 * 1000; // 30 minutes

  /**
   * Save current application state
   */
  saveState(additionalState?: Record<string, any>): void {
    try {
      const preservedState: PreservedState = {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        state: {
          ...additionalState,
          // Save any form data from localStorage/sessionStorage if needed
          ...this.extractFormData(),
        },
        timestamp: Date.now(),
      };

      sessionStorage.setItem(this.STATE_KEY, JSON.stringify(preservedState));
      console.log('Application state preserved', {
        pathname: preservedState.pathname,
        timestamp: new Date(preservedState.timestamp).toISOString(),
      });
    } catch (error) {
      console.error('Failed to save application state:', error);
    }
  }

  /**
   * Restore previously saved state
   */
  restoreState(): boolean {
    try {
      const saved = sessionStorage.getItem(this.STATE_KEY);
      if (!saved) {
        return false;
      }

      const preservedState: PreservedState = JSON.parse(saved);

      // Check if state is too old
      if (Date.now() - preservedState.timestamp > this.MAX_STATE_AGE) {
        console.log('Preserved state is too old, discarding');
        this.clearState();
        return false;
      }

      // Restore the URL
      const urlToRestore =
        preservedState.pathname + preservedState.search + preservedState.hash;

      console.log('Restoring application state', {
        url: urlToRestore,
        timestamp: new Date(preservedState.timestamp).toISOString(),
      });

      // Use replace to avoid adding to history
      window.history.replaceState(preservedState.state, '', urlToRestore);

      // Clear the preserved state after restoration
      this.clearState();

      // Restore form data if present
      if (preservedState.state?.formData) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.restoreFormData(preservedState.state.formData);
        }, 100);
      }

      // Trigger a custom event for components to restore their state
      window.dispatchEvent(
        new CustomEvent('appStateRestored', {
          detail: preservedState.state,
        })
      );

      return true;
    } catch (error) {
      console.error('Failed to restore application state:', error);
      this.clearState();
      return false;
    }
  }

  /**
   * Check if there's a preserved state to restore
   */
  hasPreservedState(): boolean {
    try {
      const saved = sessionStorage.getItem(this.STATE_KEY);
      if (!saved) {
        return false;
      }

      const preservedState: PreservedState = JSON.parse(saved);

      // Check if state is too old
      if (Date.now() - preservedState.timestamp > this.MAX_STATE_AGE) {
        this.clearState();
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear preserved state
   */
  clearState(): void {
    try {
      sessionStorage.removeItem(this.STATE_KEY);
    } catch (error) {
      console.error('Failed to clear preserved state:', error);
    }
  }

  /**
   * Extract form data from common form storage locations
   */
  private extractFormData(): Record<string, any> {
    const formData: Record<string, any> = {};

    try {
      // Try to extract from common form state keys
      const formStateKeys = [
        'draft_form_data',
        'unsaved_changes',
        'form_state',
      ];

      for (const key of formStateKeys) {
        const value = sessionStorage.getItem(key);
        if (value) {
          try {
            formData[key] = JSON.parse(value);
          } catch {
            formData[key] = value;
          }
        }
      }
    } catch (error) {
      console.error('Failed to extract form data:', error);
    }

    return formData;
  }

  /**
   * Restore form data to the page after redirect
   * This attempts to populate form fields with previously saved values
   */
  restoreFormData(formData?: Record<string, any>): void {
    if (!formData || Object.keys(formData).length === 0) {
      return;
    }

    try {
      console.log('Restoring form data:', Object.keys(formData).length, 'fields');

      // Restore to input elements
      Object.entries(formData).forEach(([name, value]) => {
        if (value === null || value === undefined) {
          return;
        }

        // Try to find input by name first
        let input = document.querySelector(`input[name="${name}"], textarea[name="${name}"], select[name="${name}"]`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
        
        // If not found, try by id
        if (!input) {
          input = document.querySelector(`#${name}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
        }

        if (input) {
          if (input instanceof HTMLInputElement) {
            if (input.type === 'checkbox' || input.type === 'radio') {
              input.checked = value === true || input.value === value;
            } else {
              input.value = String(value);
            }
            // Trigger input event to notify React/form libraries
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input instanceof HTMLTextAreaElement) {
            input.value = String(value);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input instanceof HTMLSelectElement) {
            input.value = String(value);
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Try contenteditable elements
        const contentEditable = document.querySelector(`[contenteditable="true"][name="${name}"], [contenteditable="true"]#${name}`);
        if (contentEditable) {
          contentEditable.textContent = String(value);
          contentEditable.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      // Dispatch a custom event for components to handle restoration
      window.dispatchEvent(
        new CustomEvent('formDataRestored', {
          detail: formData,
        })
      );

      console.log('Form data restoration completed');
    } catch (error) {
      console.error('Failed to restore form data:', error);
    }
  }

  /**
   * Save form data for restoration
   */
  saveFormData(key: string, data: any): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Failed to save form data for key ${key}:`, error);
    }
  }

  /**
   * Get saved form data
   */
  getFormData(key: string): any {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error(`Failed to get form data for key ${key}:`, error);
    }
    return null;
  }
}

// Export singleton instance
export const statePreservationService = new StatePreservationService();
export default statePreservationService;
